import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';

/*
 * Quita acentos y diacr\u00edticos: NFD separa cada letra de su marca y el
 * rango U+0300\u2013U+036F las elimina. Antes solo se borraba U+0300 (acento
 * grave), as\u00ed que \u00e1/\u00e9/\u00ed/\u00f3/\u00fa, \u00f1 y \u00fc sobreviv\u00edan y romp\u00edan la comparaci\u00f3n:
 * escribir "direcci\u00f3n" o "tel\u00e9fono" con tilde no encontraba respuesta,
 * que son justo las preguntas m\u00e1s frecuentes.
 *
 * Se aplica tambi\u00e9n a las palabras clave, porque muchas solo existen
 * acentuadas (\u00f6ppettider, \u00f6ffnungszeiten, b\u00fcro, dzie\u0144 dobry...) y si
 * \u00fanicamente se limpiara el texto del usuario quedar\u00edan inalcanzables.
 * Lo que importa es que ambos lados se normalicen igual.
 */
function stripDiacritics(texto) {
  return texto.toLowerCase().trim().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
}

function getRespuesta(texto, respuestas, defaultReply, noMatch) {
  const normalized = stripDiacritics(texto);
  if (!normalized) return defaultReply;
  for (const { keywords, respuesta } of respuestas) {
    if (keywords.some((k) => normalized.includes(stripDiacritics(k)))) return respuesta;
  }
  return noMatch;
}

const TYPING_SPEED_MS = 28;

function Chatbot() {
  const { language, t, getTranslation } = useLanguage();
  const respuestas = getTranslation('chatbotResponses') || [];
  const [open, setOpen] = useState(false);
  const [avatarError, setAvatarError] = useState(false);
  const [messages, setMessages] = useState([
    { id: 0, type: 'bot', text: t('chatbot.greeting'), visibleLength: null },
  ]);

  useEffect(() => {
    setMessages((m) =>
      m.length > 0 && m[0].type === 'bot'
        ? [{ ...m[0], text: t('chatbot.greeting') }, ...m.slice(1)]
        : m
    );
  }, [language, t]);
  const [input, setInput] = useState('');
  const listRef = useRef(null);
  const typingIntervalRef = useRef(null);

  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages]);

  useEffect(() => {
    return () => {
      if (typingIntervalRef.current) clearInterval(typingIntervalRef.current);
    };
  }, []);

  useEffect(() => {
    const handleOpen = () => setOpen(true);
    window.addEventListener('openChatbot', handleOpen);
    return () => window.removeEventListener('openChatbot', handleOpen);
  }, []);

  const send = () => {
    const text = input.trim();
    if (!text) return;
    const userMsg = { id: Date.now(), type: 'user', text };
    setMessages((m) => [...m, userMsg]);
    setInput('');

    const reply = getRespuesta(
      text,
      respuestas,
      t('chatbot.defaultReply'),
      t('chatbot.noMatch')
    );
    const botMsgId = Date.now() + 1;

    setMessages((m) => [...m, { id: botMsgId, type: 'bot', text: reply, typing: true }]);

    const showTypingDuration = 600 + Math.random() * 400;

    setTimeout(() => {
      if (typingIntervalRef.current) {
        clearInterval(typingIntervalRef.current);
        typingIntervalRef.current = null;
      }
      setMessages((m) =>
        m.map((msg) =>
          msg.id === botMsgId ? { ...msg, typing: false, visibleLength: 0 } : msg
        )
      );

      let len = 0;
      typingIntervalRef.current = setInterval(() => {
        len += 1;
        setMessages((m) =>
          m.map((msg) =>
            msg.id === botMsgId && msg.type === 'bot'
              ? { ...msg, visibleLength: Math.min(len, msg.text.length) }
              : msg
          )
        );
        if (len >= reply.length && typingIntervalRef.current) {
          clearInterval(typingIntervalRef.current);
          typingIntervalRef.current = null;
          setMessages((m) =>
            m.map((msg) =>
              msg.id === botMsgId ? { ...msg, visibleLength: null } : msg
            )
          );
        }
      }, TYPING_SPEED_MS);
    }, showTypingDuration);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  return (
    <>
      <div className={`chatbot-window ${open ? 'chatbot-window--open' : ''}`}>
        <div className="chatbot-header">
          {avatarError ? (
            <div className="chatbot-avatar chatbot-avatar--fallback" aria-hidden>AV</div>
          ) : (
            <img
              src="/asistente-soporte.jpg"
              alt=""
              className="chatbot-avatar"
              onError={() => setAvatarError(true)}
            />
          )}
          <div className="chatbot-header-info">
            <div className="chatbot-header-top">
              <span className="chatbot-title">{t('chatbot.title')}</span>
              <span className="chatbot-status-dot" aria-hidden />
            </div>
            <span className="chatbot-status-text">{t('chatbot.status')}</span>
          </div>
          <button
            type="button"
            className="chatbot-close"
            onClick={() => setOpen(false)}
            aria-label={t('chatbot.close')}
          >
            ×
          </button>
        </div>
        <div className="chatbot-messages" ref={listRef}>
          {messages.map((msg) => (
            <div key={msg.id} className={`chatbot-msg chatbot-msg--${msg.type}`}>
              {msg.typing ? (
                <span className="chatbot-msg-typing">
                  <span className="chatbot-msg-typing-dot" />
                  <span className="chatbot-msg-typing-dot" />
                  <span className="chatbot-msg-typing-dot" />
                </span>
              ) : (
                <span className="chatbot-msg-text">
                  {msg.visibleLength !== undefined && msg.visibleLength !== null
                    ? msg.text.slice(0, msg.visibleLength)
                    : msg.text}
                  {msg.visibleLength !== undefined &&
                    msg.visibleLength !== null &&
                    msg.visibleLength < msg.text.length && (
                      <span className="chatbot-msg-cursor" aria-hidden />
                    )}
                </span>
              )}
            </div>
          ))}
        </div>
        <div className="chatbot-input-wrap">
          <input
            type="text"
            className="chatbot-input"
            placeholder={t('chatbot.placeholder')}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
          />
          <button type="button" className="chatbot-send" onClick={send} aria-label={t('chatbot.send')}>
            →
          </button>
        </div>
        <p className="chatbot-footer-hint">
          <Link to="/contacto" onClick={() => setOpen(false)}>{t('chatbot.footerLink')}</Link>
        </p>
      </div>
      <button
        type="button"
        className="chatbot-fab"
        onClick={() => setOpen(!open)}
        aria-label={open ? t('chatbot.close') : t('chatbot.open')}
        aria-expanded={open}
      >
        <span className="chatbot-fab-icon">{open ? '×' : '💬'}</span>
      </button>
    </>
  );
}

export default Chatbot;
